import { useContext, useEffect, useState } from "react"

import { Box, Button, Container, MobileStepper, Typography } from '@mui/material'
import { useTranslation } from "react-i18next"

import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Spacer from "../../../atoms/Spacer/Spacer"
import { v4 as uuidv4 } from 'uuid'

import { logLandingPageAction } from "../../../../clients/logger"
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"

import styles from './SubscriptionGiftCard.module.css'
import SubscriptionGiftStepperStep from "../../../molecules/SubscriptionGiftStepper/SubscriptionGiftStepper"
import useGoogleTagManager from "../../../../helpers/analytics/use-google-tag-manager"
import GtmEventName from "../../../../helpers/analytics/GtmEventName"
import { Route } from "../../../../router"
import { useNavigate } from "react-router-dom"
import SwipeableViews from "react-swipeable-views"
import { useSubscriptionGiftRepository } from "../../../../repositories/SubscriptionGift/use-subscription-gift-repository"
import { AppContext } from "../../../../stores/App/AppStore"
import { UserContext } from "../../../../stores/User/UserStore"

const SubscriptionGiftCard = () => {

    const { t } = useTranslation()
    const { trackButtonClick } = useGoogleTagManager()
    const { retrieveGiftCards, createGiftPaymentOrder } = useSubscriptionGiftRepository()
    const { state: appState } = useContext(AppContext)
    const { state: userState } = useContext(UserContext)
    const [recipientMessage, setRecipientMessage] = useState(``)
    const [activeStep, setActiveStep] = useState(0)
    const maxSteps = appState.giftCards.length

    useEffect(() => {
        retrieveGiftCards()
    }, [])

    const handlePreviousStepClick = () => {
        setActiveStep(Math.max(activeStep - 1, 0))
    }
    const handleNextStepClick = () => {
        // eslint-disable-next-line no-magic-numbers
        setActiveStep(Math.min(activeStep + 1, maxSteps - 1))
    }

    const navigate = useNavigate()

    useTrackPageView(`Subscription Gift Card Page`)

    const handleSubmit = () => {
        trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_CARD_SELECTED)
        logLandingPageAction(`User pressed review and pay on gift card selection`, `myInfo`)
        userState.subscriptionGift = {
            ...userState.subscriptionGift,
            giftCardTitle: appState.giftCards[activeStep].title,
            recipientMessage,
            code: uuidv4(),
        }
        createGiftPaymentOrder(userState.subscriptionGift)
        navigate(`${Route.SUBSCRIPTION_GIFT_SENDER_PAYMENT}`)
    }

    return (
        <Container>
            <CommonBackButton />
            <Spacer height={24} />
            <SubscriptionGiftStepperStep activeStep={2}/>
            <Spacer height={36} />
            <Typography variant="h3" className={styles.choose_gift_card_header}>{t(`subscription_gift_choose_gift_card`)}</Typography>
            <Spacer height={12} />
            {maxSteps > 0 && <Box className={styles.container}>
                <SwipeableViews enableMouseEvents index={activeStep}>
                    {appState.giftCards.map((giftCard) => {
                        return (
                            <Box display="flex" key={giftCard.title} justifyContent="center" alignItems="center">
                                <Box
                                    component="img"
                                    src={giftCard.giftCard}/>
                            </Box>
                        )
                    })
                    }
                </SwipeableViews>
                <MobileStepper
                    className={styles.mobileStepper}
                    steps={maxSteps}
                    position={`static`}
                    activeStep={activeStep}
                    nextButton={<ChevronRightIcon color='primary' onClick={handleNextStepClick} />}
                    backButton={<ChevronLeftIcon color='primary' onClick={handlePreviousStepClick} />}
                />
            </Box>}
            <Spacer height={24} />
            <Typography variant="h6">{t(`subscription_gift_recipient_message`)}</Typography>
            <Spacer height={12} />
            <textarea name="postContent"
                rows={6} cols={40}
                value={recipientMessage}
                onChange={value => setRecipientMessage(value.target.value)} />
            <Spacer height={24} />
            <Button variant='contained' onClick={handleSubmit} fullWidth>{t(`subscription_gift_review_and_pay`)}</Button>
        </Container>
    )
}

export default SubscriptionGiftCard